import { useRef, useState, type Dispatch, type SetStateAction } from 'react';

import type { PrivateEventFormFields } from './private-event-form-state';
import { submitPrivateEvent } from './private-event-submit';
import type { StepErrors } from './private-event-validation';

interface Args {
  fields: PrivateEventFormFields;
  onCreated: (id: string) => void;
  /** Writes every step's errors and returns the first failing step, or null. */
  firstInvalidStep: () => number | null;
  setFieldErrors: Dispatch<SetStateAction<StepErrors>>;
  setStep: (i: number) => void;
}

/** Validates every step, then sends the create. A second call while one is
 *  running is ignored. */
export function useSubmitPrivateEvent(args: Args) {
  const { fields, onCreated, firstInvalidStep, setFieldErrors, setStep } = args;
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // A ref, not the `submitting` state: two quick Enters land before a render.
  const inFlight = useRef(false);

  /** Resolves to the step that failed validation, or null when it was sent (or
   *  ignored because a create is already running). */
  const submit = async (): Promise<number | null> => {
    if (inFlight.current) return null;
    const failing = firstInvalidStep();
    if (failing !== null) {
      setFormError(null);
      setStep(failing);
      return failing;
    }
    inFlight.current = true;
    try {
      await submitPrivateEvent({
        fields,
        onCreated,
        setSubmitting,
        setFormError,
        setFieldErrors,
        setStep,
      });
    } finally {
      inFlight.current = false;
    }
    return null;
  };

  return { submit, submitting, formError };
}
